import {
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import * as dayjs from 'dayjs';
import {
	UserService,
	userServiceToken
} from 'go-modules/models/user/user.service';
import { selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { SelectedService } from 'go-modules/services/selected/selected.service';
import { CookieService } from 'ngx-cookie-service';
import type { Comment } from '@go-modules/interfaces/comments/comment';
import { NgxCommentService } from '@go-modules/services/comment/comment.service';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import {
	UADetect as UADetectClass,
	uaDetectToken
} from 'go-modules/detect/ua-detect.service';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogData } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { EventService } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { MODES } from '@go-modules/components/feedback-session/comment-reactions/comment-reactions.component';
import {
	commentModelToken,
	CommentModel
} from 'go-modules/models/comment/comment.factory';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ViewersOverlayComponent } from './comment-viewers/comment-viewers.component';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface CommentViewer {
	name: string;
	viewed_at: string;
}

@Component({
	selector: 'ngx-comment-menu',
	templateUrl: './comment-menu.component.html',
	styleUrls: ['./comment-menu.component.scss']
})
export class NgxCommentMenuComponent implements OnInit, OnDestroy {
	@Input() public mode: string;
	@Input() public feedbackItem: Comment;
	@Input() public hideCommentReply: boolean;
	@Output() public edit: EventEmitter<Comment> = new EventEmitter<Comment>();
	@Output() public setSelectedComment: EventEmitter<Comment> = new EventEmitter<Comment>();
	@ViewChild('overlayOrigin') private overlayOrigin: ElementRef;
	public modes = MODES;
	public isSelfDemo: boolean = false;
	public environmentVarsService: EnvironmentVarsService;
	public viewersLoading: boolean = false;
	public viewers: CommentViewer[] = [];
	private overlayRef: OverlayRef;
	private destroy$ = new Subject<void>();

	constructor (
		@Inject(userServiceToken) private userService: UserService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(uaDetectToken) private UADetect: UADetectClass,
		@Inject(commentModelToken) private Comment: ReturnType<typeof CommentModel>,
		public eventService: EventService,
		public cookieService: CookieService,
		private commentService: NgxCommentService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private overlay: Overlay,
		public commentListService: CommentListService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public ngOnInit (): void {
		this.isSelfDemo =
			this.cookieService.get('is_self_demo_experiment') === 'true';
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();

		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = null;
		}
	}

	// Functions to determine if menu options should be shown

	public showReplyButton = (): boolean => {
		if (this.feedbackItem.parent_id) {
			return false;
		}

		if (this.hideCommentReply) {
			return false;
		}

		// User needs to be able to have same permission as creating a session
		const group = this.selectedService.getGroup();
		const license = this.selectedService.getLicense();
		return group.validateCanCreateSession(this.userService.currentUser, license)
			.isValid;
	};

	public mayEdit = (): boolean => {
		if (this.isEditDisabled()) {
			return false;
		}

		const group = this.selectedService.getGroup();
		return (
			group.hasInstructorRole(true) ||
			(group.hasReviewerRole(true) && this.feedbackItem.was_ai_generated) ||
			Number(this.feedbackItem.created_by) === Number(this.userService.currentUser.user_id)
		);
	};

	public isEditDisabled = (): boolean => {
		const session = this.selectedService.getSession();
		const activity = this.selectedService.getActivity();
		const user = this.userService.currentUser;
		if (session.isLimitCommentOnlySingleAttempt(activity, user)) {
			return true;
		}

		const group = this.selectedService.getGroup();
		const license = this.selectedService.getLicense();
		return !group.validateCanCreateSession(user, license).isValid;
	};

	public shouldShowManageAiMarkersButton () {
		const activity = this.selectedService.getActivity();
		const group = this.selectedService.getGroup();

		return (
			group.hasInstructorRole(true) &&
			this.feedbackItem.was_ai_generated &&
			!(activity.is_parent_sync_enabled && activity.parent_child_sync) &&
			!this.environmentVarsService.get(
				EnvironmentVarsService.VAR.DISABLE_ACTIVITY_EDITING
			)
		);
	}

	// Functions to handle actions

	public reply = () => {
		this.setSelectedComment.emit(this.feedbackItem);
		const commentReply = this.Comment.getReplyStub(
			this.feedbackItem,
			this.userService.currentUser
		);
		this.commentListService.addCommentReply(this.feedbackItem, commentReply);
		this.edit.emit(commentReply);
	};

	public confirmRemove = () => {
		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> =
			this.dialog.open(ConfirmDialogComponent, {
				data: {
					title: this.translate.instant(
						'feedback-session-feedback-tree-comment_sure-remove-comment-title'
					),
					message: this.translate.instant(
						'feedback-session-feedback-tree-comment_sure-remove-comment'
					),
					confirmText: this.translate.instant('common_remove'),
					cancelText: this.translate.instant('common_cancel')
				} as ConfirmDialogData
			});

		dialogRef.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((confirm) => {
				if (confirm) {
					this.remove();
				}
			});
	};

	public remove = () => {
		this.commentListService.removeComment(this.feedbackItem);

		if (!this.feedbackItem.comment_id) {
			return;
		}

		const session = this.selectedService.getSession();
		this.commentService.deleteComment(session.session_id, this.feedbackItem.comment_id)
			.pipe(takeUntil(this.destroy$))
			.subscribe();
	};

	public openAiMarkers ($event: MouseEvent) {
		$event.stopPropagation();
		this.eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_OPEN_AI_MARKERS);
	}

	public getCommentViewers ($event: MouseEvent, feedbackItem: Comment) {
		$event.stopPropagation();
		this.setSelectedComment.emit(feedbackItem);
		if (!this.viewers.length) {
			const session = this.selectedService.getSession();
			this.viewersLoading = true;
			this.commentService
				.getViewers(session.session_id, feedbackItem.comment_id)
				.pipe(takeUntil(this.destroy$))
				.subscribe((viewers) => {
					viewers.forEach((viewer) => {
						viewer.viewed_at = this.UADetect.isMobile()
							? dayjs(viewer.viewed_at).format('M/D/YY h:mm A')
							: dayjs(viewer.viewed_at).format('MMM D, YYYY h:mm A');
					});
					viewers.sort((a, b) =>
						dayjs(b.viewed_at).isAfter(dayjs(a.viewed_at)) ? 1 : -1
					);
					this.viewersLoading = false;
					this.viewers = viewers;

					this.updateComponentRef();
				});
		} else {
			this.updateComponentRef();
		}
	}

	private updateComponentRef () {
		if (!this.overlayRef) {
			const positionStrategy = this.overlay
				.position()
				.flexibleConnectedTo(this.overlayOrigin)
				.withPositions([
					{
						originX: 'start',
						originY: 'bottom',
						overlayX: 'start',
						overlayY: 'top'
					}
				]);

			this.overlayRef = this.overlay.create({
				positionStrategy,
				hasBackdrop: true,
				backdropClass: 'cdk-overlay-transparent-backdrop',
				panelClass: 'viewers-overlay-panel',
				scrollStrategy: this.overlay.scrollStrategies.close()
			});

			this.overlayRef.backdropClick()
				.pipe(takeUntil(this.destroy$))
				.subscribe(() => {
					if (this.overlayRef) {
						this.overlayRef.detach();
					}
				});
		}

		const viewersPortal = new ComponentPortal(ViewersOverlayComponent);
		const componentRef = this.overlayRef.attach(viewersPortal);
		componentRef.instance.viewers = this.viewers;
		componentRef.instance.viewersLoading = this.viewersLoading;
	}
}
